'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { InstitutionEffectiveRole } from '@/lib/institution-roles';

const STORAGE_KEY = 'ertzyx-institution-workspace';

export type InstitutionWorkspaceCandidate = {
  institutionId: string;
  institutionName: string;
  role: string;
};

export type InstitutionWorkspaceState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'no-membership' }
  | { status: 'multiple'; candidates: InstitutionWorkspaceCandidate[] }
  | {
      status: 'ready';
      institutionId: string;
      institutionName: string;
      institutionType: string | null;
      publicHandle: string | null;
      publicProfilePublished: boolean;
      logoUrl: string | null;
      role: InstitutionEffectiveRole;
      isOwner: boolean;
      actorName: string;
      workspaces: InstitutionWorkspaceCandidate[];
    };

type InstitutionWorkspaceContextValue = InstitutionWorkspaceState & {
  refresh: () => void;
  selectWorkspace: (institutionId: string) => void;
};

const InstitutionWorkspaceContext = createContext<InstitutionWorkspaceContextValue | null>(null);

function readStoredWorkspaceId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredWorkspaceId(institutionId: string | null) {
  try {
    if (institutionId) window.localStorage.setItem(STORAGE_KEY, institutionId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — resolution still works, just without persistence across reloads
  }
}

/** Pre-selects the active institution workspace before InstitutionWorkspaceProvider mounts —
 * used right after accepting an institution invitation so the dashboard opens directly into the
 * institution just joined instead of prompting a redundant workspace choice. */
export function persistSelectedInstitutionWorkspace(institutionId: string) {
  writeStoredWorkspaceId(institutionId);
}

export function InstitutionWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<InstitutionWorkspaceState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(async (preferredInstitutionId?: string | null) => {
    setState({ status: 'loading' });
    const stored = preferredInstitutionId ?? readStoredWorkspaceId();
    const query = stored ? `?institution_id=${encodeURIComponent(stored)}` : '';

    let res: Response;
    try {
      res = await fetch(`/api/institution/workspace${query}`, { cache: 'no-store' });
    } catch {
      setState({
        status: 'error',
        message: 'Could not reach institution services. Check your connection.',
      });
      return;
    }

    if (res.ok) {
      const body = (await res.json().catch(() => null)) as {
        institutionId: string;
        institutionName: string;
        institutionType: string | null;
        publicHandle: string | null;
        publicProfilePublished: boolean;
        logoUrl: string | null;
        role: InstitutionEffectiveRole;
        isOwner: boolean;
        actorName: string;
        workspaces: InstitutionWorkspaceCandidate[];
      } | null;
      if (!body) {
        setState({ status: 'error', message: 'Institution workspace response was invalid.' });
        return;
      }
      writeStoredWorkspaceId(body.institutionId);
      setState({ status: 'ready', ...body });
      return;
    }

    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      workspaces?: InstitutionWorkspaceCandidate[];
    };

    if (res.status === 409 && body.code === 'multiple_memberships') {
      setState({ status: 'multiple', candidates: body.workspaces ?? [] });
      return;
    }

    if (res.status === 403) {
      // A previously-stored selection may no longer be an active membership (revoked, or the
      // institution itself was deactivated). Fall back once to unscoped resolution instead of
      // getting permanently stuck on a dead selection.
      if (stored) {
        writeStoredWorkspaceId(null);
        await load(null);
        return;
      }
      setState({ status: 'no-membership' });
      return;
    }

    setState({
      status: 'error',
      message: body.error || 'Could not resolve your institution workspace.',
    });
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  const selectWorkspace = useCallback(
    (institutionId: string) => {
      writeStoredWorkspaceId(institutionId);
      void load(institutionId);
    },
    [load]
  );

  const value = useMemo<InstitutionWorkspaceContextValue>(
    () => ({ ...state, refresh, selectWorkspace }),
    [state, refresh, selectWorkspace]
  );

  return (
    <InstitutionWorkspaceContext.Provider value={value}>
      {children}
    </InstitutionWorkspaceContext.Provider>
  );
}

/** Resolved institution tenant for the authenticated caller (owner or delegated member). Throws
 * outside InstitutionWorkspaceProvider so a missing provider fails loudly during development
 * instead of silently falling back to the caller's own id. */
export function useInstitutionWorkspaceContext(): InstitutionWorkspaceContextValue {
  const ctx = useContext(InstitutionWorkspaceContext);
  if (!ctx) {
    throw new Error(
      'useInstitutionWorkspaceContext must be used within InstitutionWorkspaceProvider'
    );
  }
  return ctx;
}
